// Library imports
import React, { forwardRef, memo, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";

// User Component imports
import { useDebouncedValue } from "../common/useDebouncedValue";
import { PROMPT_TABS } from "../utils/promptVersions";
import { diffLineMarks } from "../utils/promptDiff";
import { applyMdTool, parseHeadings, listKindOf, listEnter, listBackspace, tabIndent } from "../utils/promptMarkdown";
import { variableQueryAt, filterVariables, insertVariable } from "../utils/promptVariables";
import VariableMenu from "./VariableMenu";

// 行送り(px)とテキスト上端の余白。textarea / ガター / 警告帯で必ずそろえる（小数にすると数百行でずれる）
const LINE_HEIGHT = 21;
const PAD_TOP = 12;
// 入力中に新しく出た指摘を出すまでの待ち時間(ms)
const LINT_IDLE_MS = 800;
const MARKS_DELAY = 200;
// 変数の候補リストの幅と、上下どちらに出すかを決めるための最大の高さ(px)
const VAR_MENU_WIDTH = 320;
const VAR_MENU_MAX_HEIGHT = 280;

const SEVERITY_ICON = { error: "bi-x-octagon-fill", warn: "bi-exclamation-triangle-fill" };

// 行番号ガターの1行（変わらない行は再描画しない）
const GutterLine = memo(({ num, mark, sev }) => (
    <div className={"ops-gutter-line" + (sev ? " is-" + sev : "")}>
        <div className={"ops-gutter-bar" + (mark ? " is-" + mark : "")} />
        <div className="ops-gutter-num">{num}</div>
    </div>
));

// 見出しアウトライン（見出し・現在位置・指摘が変わらない限り再描画しない）
const PromptOutline = memo(({ headings, currentIndex, headingWarn, jumpRef }) => (
    <div className="ops-outline">
        <div className="ops-outline-title">アウトライン</div>
        {!headings.length && <div className="ops-outline-empty">見出しがありません</div>}
        {headings.map((h, i) => (
            <button key={h.line} type="button" className={"ops-outline-item" + (i === currentIndex ? " is-current" : "")}
                style={{ paddingLeft: (8 + (h.level - 1) * 12) + "px" }} title={(headingWarn[h.line] || []).join("\n")} onClick={() => jumpRef.current(h)}>
                <span className="ops-outline-level">H{h.level}</span>
                <span className="ops-outline-text">{h.text || "（空の見出し）"}</span>
                {headingWarn[h.line] && <i className="bi bi-exclamation-triangle-fill ops-outline-warn" />}
            </button>
        ))}
    </div>
));

// 編集欄（本文が変わらない限り再描画しない）
const PromptTextarea = memo(({ taRef, text, placeholder, onChange, onCaret, onScroll, onKeyDown, onBlur }) => (
    <textarea
        ref={taRef}
        className="ops-editor-textarea"
        value={text}
        placeholder={placeholder}
        spellCheck={false}
        wrap="off"
        onChange={(e) => { onChange(e.target.value); onCaret(e.target.selectionStart); }}
        onSelect={(e) => onCaret(e.target.selectionStart)}
        onClick={(e) => onCaret(e.target.selectionStart)}
        onKeyUp={(e) => onCaret(e.target.selectionStart)}
        onKeyDown={onKeyDown}
        onScroll={onScroll}
        onBlur={onBlur}
    />
));

// 本文の差し替えを「変わった範囲だけの挿入」にして、Ctrl+Z で戻せるようにする
const replaceWithUndo = (ta, nextText, onChange) => {
    const prev = ta.value;
    let start = 0;
    while (start < prev.length && start < nextText.length && prev[start] === nextText[start]) start += 1;
    let endPrev = prev.length;
    let endNext = nextText.length;
    while (endPrev > start && endNext > start && prev[endPrev - 1] === nextText[endNext - 1]) { endPrev -= 1; endNext -= 1; }
    ta.focus();
    ta.setSelectionRange(start, endPrev);
    const inserted = document.execCommand && document.execCommand("insertText", false, nextText.slice(start, endNext));
    if (!inserted || ta.value !== nextText) onChange(nextText);
};

/**
 * システム／ユーザーの片方のペイン（ヘッダー・アウトライン・行番号・警告帯・編集欄）
 * 書式ツールと変数の挿入はツールバーから ref 経由（applyTool / insertVariable）で呼ぶ
 */
const PromptPane = forwardRef(({ tab, groupKey, text, remoteText, issues, focused, variables, outlineOpen, className = "", style,
    onChange, onFocusPane, onDiscard, onActiveInfo, onMenuKey, onToggleOutline }, ref) => {
    const taRef = useRef(null);
    const gutterRef = useRef(null);
    const bandRef = useRef(null);
    const areaRef = useRef(null);
    const measureRef = useRef(null);
    const [caret, setCaret] = useState(0);
    const [typing, setTyping] = useState(false);
    const typingTimer = useRef(null);
    const shownKeys = useRef(new Set());
    // { の入力に続けて出す変数の候補
    const [assist, setAssist] = useState(null);
    const [, setScrollTick] = useState(0);
    const assistItems = !assist || !variables ? [] : filterVariables(variables, assist.query);
    const title = PROMPT_TABS.find((t) => t.key === tab)?.label ?? "";
    const edited = text !== remoteText;

    const syncScroll = () => {
        const ta = taRef.current;
        const y = ta ? ta.scrollTop : 0;
        const transform = "translateY(" + -y + "px)";
        if (gutterRef.current) gutterRef.current.style.transform = transform;
        if (bandRef.current) bandRef.current.style.transform = transform;
    };

    // グループを切り替えたら先頭に戻す
    useLayoutEffect(() => {
        const ta = taRef.current;
        if (ta) ta.scrollTop = 0;
        syncScroll();
        setCaret(0);
        setAssist(null);
    }, [groupKey]);

    const handleChange = (value) => {
        onChange(tab, value);
        // { に続けて入力しているあいだは変数の候補を出す
        const ta = taRef.current;
        const query = variables && ta ? variableQueryAt(value, ta.selectionStart) : null;
        setAssist(query && filterVariables(variables, query.query).length ? { ...query, index: 0 } : null);
        setTyping(true);
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setTyping(false), LINT_IDLE_MS);
    };

    useEffect(() => () => clearTimeout(typingTimer.current), []);

    // キャレットだけ動いたときは候補を絞り直し、入力途中の変数から外れたら閉じる
    useEffect(() => {
        if (!assist) return;
        const query = variableQueryAt(text, caret);
        if (!query || query.start !== assist.start) setAssist(null);
        else if (query.query !== assist.query) setAssist({ ...assist, query: query.query, index: 0 });
    }, [assist, text, caret]);

    // 候補リストの外をクリックしたら閉じる
    useEffect(() => {
        if (!assist) return undefined;
        const onDown = (e) => {
            if (!e.target.closest(".ops-var-menu")) setAssist(null);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [assist]);

    // 入力中は新しく発生した指摘を出さず、手が止まってから表示する
    const shownIssues = useMemo(() => {
        const keyOf = (it) => it.line + "|" + it.short;
        if (typing) return issues.filter((it) => shownKeys.current.has(keyOf(it)));
        shownKeys.current = new Set(issues.map(keyOf));
        return issues;
    }, [issues, typing]);

    const byLine = useMemo(() => {
        const map = {};
        shownIssues.forEach((it) => {
            const cur = map[it.line] || (map[it.line] = { sev: "warn", msgs: [], shorts: [] });
            if (it.sev === "error") cur.sev = "error";
            cur.msgs.push(it.msg);
            cur.shorts.push(it.short);
        });
        return map;
    }, [shownIssues]);
    const errShown = shownIssues.filter((it) => it.sev === "error").length;
    const warnShown = shownIssues.length - errShown;
    const lintTitle = shownIssues.map((it) => (it.line + 1) + "行目: " + it.msg).join("\n");

    // 差分の色帯（本文が変わったときだけ計算する）
    const debouncedText = useDebouncedValue(text, MARKS_DELAY);
    // 本文が適用中と同じになったら待たずに色帯を消す
    const markSource = text === remoteText ? text : debouncedText;
    const marks = useMemo(() => diffLineMarks(remoteText, markSource), [remoteText, markSource]);
    const markCount = marks.filter((m) => m).length;

    const lineCount = text.split("\n").length;
    const gutterWidth = Math.round(3 + 6 + Math.max(2, String(lineCount).length) * 6.7 + 8);

    // アウトラインと現在の見出し
    const headings = useMemo(() => parseHeadings(debouncedText), [debouncedText]);
    let currentIndex = -1;
    headings.forEach((h, i) => { if (h.index <= caret) currentIndex = i; });
    const currentLevel = currentIndex >= 0 ? headings[currentIndex].level : 0;
    // キャレット行のリスト種別（- / * / + の箇条書き、番号付き）
    const caretLineStart = text.lastIndexOf("\n", caret - 1) + 1;
    const caretLineEnd = text.indexOf("\n", caret);
    const currentListKind = listKindOf(text.slice(caretLineStart, caretLineEnd < 0 ? text.length : caretLineEnd));
    const headingWarn = useMemo(() => {
        const map = {};
        issues.forEach((it) => { if (it.sev === "warn") (map[it.line] = map[it.line] || []).push(it.msg); });
        return map;
    }, [issues]);

    // ツールバーの選択状態（見出しレベル・リスト種別）を親へ知らせる
    useEffect(() => {
        onActiveInfo(tab, { currentLevel, currentListKind });
    }, [tab, currentLevel, currentListKind, onActiveInfo]);

    // 行番号ガター（行数・色帯・指摘が変わったときだけ作り直す）
    const gutterLines = useMemo(() => Array.from({ length: lineCount }, (_, i) => (
        <GutterLine key={i} num={i + 1} mark={marks[i]} sev={byLine[i]?.sev} />
    )), [lineCount, marks, byLine]);

    const jumpRef = useRef(null);
    jumpRef.current = (h) => {
        const ta = taRef.current;
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(h.index, h.index);
        ta.scrollTop = Math.max(0, h.line * LINE_HEIGHT - 60);
        syncScroll();
        setCaret(h.index);
    };

    // 整形結果を元に戻せる形で反映し、選択範囲を復元する
    const applyResult = (ta, result) => {
        replaceWithUndo(ta, result.text, handleChange);
        requestAnimationFrame(() => {
            const t = taRef.current;
            if (!t) return;
            t.focus();
            t.setSelectionRange(result.selStart, result.selEnd);
            setCaret(result.selStart);
        });
    };

    // 変数を確定する。入力途中の {xx は置き換え、ツールバーからはキャレット位置（選択中なら置き換え）に入れる
    const pickVariable = (name, fromMenu) => {
        const ta = taRef.current;
        if (!ta) return;
        let start = ta.selectionStart;
        const end = ta.selectionEnd;
        if (!fromMenu && assist) {
            start = assist.start;
        } else if (start === end) {
            const query = variableQueryAt(ta.value, start);
            if (query) start = query.start;
        }
        setAssist(null);
        applyResult(ta, insertVariable(ta.value, start, end, name));
    };

    const applyTool = (tool) => {
        const ta = taRef.current;
        if (!ta) return;
        applyResult(ta, applyMdTool(ta.value, ta.selectionStart, ta.selectionEnd, tool));
    };

    useImperativeHandle(ref, () => ({
        applyTool,
        insertVariable: (name) => pickVariable(name, true),
        focus: () => taRef.current && taRef.current.focus(),
    }));

    const onKeyDown = (e) => {
        const ta = taRef.current;
        if (!ta) return;
        // ツールバーから開いた変数の候補を優先する
        if (onMenuKey && onMenuKey(e)) return;
        const plain = !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && !e.nativeEvent.isComposing && e.keyCode !== 229;
        // 変数の候補を開いているあいだは ↑↓ / Enter・Tab / Esc を候補の操作に使う
        if (assist && assistItems.length && plain) {
            const count = assistItems.length;
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const step = e.key === "ArrowDown" ? 1 : -1;
                setAssist({ ...assist, index: (assist.index + step + count) % count });
                return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                pickVariable(assistItems[Math.min(assist.index, count - 1)], false);
                return;
            }
            if (e.key === "Escape") {
                e.preventDefault();
                setAssist(null);
                return;
            }
        }
        // リスト行の Enter / Backspace（IME 変換中と修飾キー併用は通常の動き）
        if (plain && (e.key === "Enter" || e.key === "Backspace")) {
            const result = (e.key === "Enter" ? listEnter : listBackspace)(ta.value, ta.selectionStart, ta.selectionEnd);
            if (result) {
                e.preventDefault();
                applyResult(ta, result);
            }
            return;
        }
        // Tab / Shift+Tab は半角スペース2つ分のインデント
        if (e.key === "Tab" && !e.ctrlKey && !e.metaKey && !e.altKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            applyResult(ta, tabIndent(ta.value, ta.selectionStart, ta.selectionEnd, e.shiftKey));
            return;
        }
        if (!(e.ctrlKey || e.metaKey)) return;
        const key = e.key.toLowerCase();
        if (key === "b") { e.preventDefault(); applyTool({ kind: "bold" }); }
        if (key === "k") { e.preventDefault(); applyTool({ kind: "link" }); }
    };

    // 編集欄の memo を効かせるため、毎回作り直す関数は ref 経由の固定関数で渡す
    const handlersRef = useRef({});
    // 候補はキャレットの位置に出しているため、スクロールしたら位置を取り直し、フォーカスが外れたら閉じる
    handlersRef.current = {
        onChange: handleChange,
        onScroll: () => {
            syncScroll();
            if (assist) setScrollTick((n) => n + 1);
        },
        onKeyDown,
        onBlur: () => assist && setAssist(null),
    };
    const stableHandlers = useMemo(() => ({
        onChange: (value) => handlersRef.current.onChange(value),
        onScroll: () => handlersRef.current.onScroll(),
        onKeyDown: (e) => handlersRef.current.onKeyDown(e),
        onBlur: () => handlersRef.current.onBlur(),
    }), []);

    // 入力途中の変数の { の真下に候補を出す（折り返し無し・行高固定なので行番号と行頭からの文字幅で求まる）
    const autoMenuStyle = () => {
        const ta = taRef.current;
        const area = areaRef.current;
        if (!ta || !area) return null;
        const lineStart = text.lastIndexOf("\n", assist.start - 1) + 1;
        let line = 0;
        for (let i = text.indexOf("\n"); i >= 0 && i < lineStart; i = text.indexOf("\n", i + 1)) line += 1;
        const cs = getComputedStyle(ta);
        const ctx = measureRef.current || (measureRef.current = document.createElement("canvas").getContext("2d"));
        ctx.font = cs.font;
        const prefix = text.slice(lineStart, assist.start).replace(/\t/g, "  ");
        const x = parseFloat(cs.paddingLeft) + ctx.measureText(prefix).width - ta.scrollLeft;
        const lineTop = PAD_TOP + line * LINE_HEIGHT - ta.scrollTop;
        // 入力位置が見えないところまでスクロールしたら隠す
        if (lineTop + LINE_HEIGHT < 0 || lineTop > area.clientHeight) return { display: "none" };
        const width = Math.min(VAR_MENU_WIDTH, area.clientWidth - 16);
        const left = Math.max(8, Math.min(x, area.clientWidth - width - 8));
        const below = lineTop + LINE_HEIGHT;
        // 下に収まらず上に余裕があるときは行の上に出す
        if (below + VAR_MENU_MAX_HEIGHT > area.clientHeight && lineTop > area.clientHeight - below) {
            return { left, bottom: area.clientHeight - lineTop + 2, width };
        }
        return { left, top: below + 2, width };
    };

    const focusEditor = () => taRef.current && taRef.current.focus();

    return (
        <div className={"ops-pane" + (focused ? " is-focused" : "") + " " + className} style={style} onFocus={() => onFocusPane(tab)}>
            <div className="ops-pane-head" onClick={focusEditor}>
                <span className="ops-pane-title">
                    <button type="button" className={"ops-pane-outline-btn" + (outlineOpen ? " is-open" : "")}
                        title={outlineOpen ? "アウトラインを閉じます" : "アウトラインを開きます"}
                        onClick={(e) => { e.stopPropagation(); onToggleOutline(tab); }}>
                        <i className="bi bi-list-nested" />
                    </button>
                    {/* 見出しの文字を押してもアウトラインを開閉する（編集欄へのフォーカスはヘッダーと同じ） */}
                    <span className="ops-pane-title-text" title={outlineOpen ? "アウトラインを閉じます" : "アウトラインを開きます"}
                        onClick={() => onToggleOutline(tab)}>{title}</span>
                    {edited && <span className="ops-pane-dot" title="未保存の編集があります" />}
                </span>
                <span className="ops-pane-stats">
                    {errShown > 0 && <span className="ops-lint-count is-error" title={lintTitle}><i className="bi bi-x-octagon-fill" /><span>{errShown}</span></span>}
                    {warnShown > 0 && <span className="ops-lint-count is-warn" title={lintTitle}><i className="bi bi-exclamation-triangle-fill" /><span>{warnShown}</span></span>}
                    <span className="ops-char-count">{text.length.toLocaleString()} 文字</span>
                    <span className={"ops-diff-summary" + (markCount ? " is-diff" : "")} title={markCount ? markCount + " 行が適用中と相違" : "適用中と一致"}>
                        {markCount ? markCount + " 行相違" : "適用中と一致"}
                    </span>
                    {edited &&
                        <button type="button" className="ops-pane-discard" title={`この${tab === "sys" ? "システム" : "ユーザー"}プロンプトの編集を破棄して、適用中の内容に戻します`}
                            onClick={(e) => { e.stopPropagation(); onDiscard(tab); }}>
                            <i className="bi bi-arrow-counterclockwise" /><span>編集破棄</span>
                        </button>
                    }
                </span>
            </div>

            <div className="ops-pane-main">
                {outlineOpen && <PromptOutline headings={headings} currentIndex={currentIndex} headingWarn={headingWarn} jumpRef={jumpRef} />}

                <div className="ops-gutter" style={{ width: gutterWidth, minWidth: gutterWidth }}>
                    <div ref={gutterRef} className="ops-gutter-inner" style={{ paddingTop: PAD_TOP }}>
                        {gutterLines}
                    </div>
                </div>

                <div className="ops-editor-area" ref={areaRef}>
                    <div className="ops-band-layer" aria-hidden="true">
                        <div ref={bandRef} className="ops-band-inner">
                            {Object.keys(byLine).map((line) => {
                                const b = byLine[line];
                                return (
                                    <div key={line} className={"ops-band is-" + b.sev} style={{ top: (PAD_TOP + Number(line) * LINE_HEIGHT) + "px" }}>
                                        <span className="ops-band-msg" title={b.msgs.join("\n")}>
                                            <i className={"bi " + SEVERITY_ICON[b.sev]} /><span>{b.shorts.join(" ／ ")}</span>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <PromptTextarea
                        taRef={taRef}
                        text={text}
                        placeholder={tab === "sys" ? "システムプロンプトを入力…" : "ユーザープロンプトを入力…"}
                        onChange={stableHandlers.onChange}
                        onCaret={setCaret}
                        onScroll={stableHandlers.onScroll}
                        onKeyDown={stableHandlers.onKeyDown}
                        onBlur={stableHandlers.onBlur}
                    />
                    {assist && assistItems.length > 0 &&
                        <VariableMenu items={assistItems} activeIndex={Math.min(assist.index, assistItems.length - 1)}
                            onSelect={(name) => pickVariable(name, false)}
                            onHover={(index) => setAssist((prev) => (prev ? { ...prev, index } : prev))}
                            style={autoMenuStyle()} />
                    }
                </div>
            </div>
        </div>
    );
});

export default PromptPane;
